<script lang="ts">
	import { TEST_SET } from '#lib/data/testSet';
	import { t } from '#lib/i18n/state.svelte';
	import { onMount } from 'svelte';

	const STORAGE_KEY = 'repcheck.trials.v1';

	let checked = $state<Record<string, boolean>>({});
	let mounted = $state(false);

	onMount(() => {
		try {
			const raw = localStorage.getItem(STORAGE_KEY);
			if (raw) checked = JSON.parse(raw);
		} catch {
			/* localStorage kullanılamıyorsa sessizce geç */
		}
		mounted = true;
	});

	function toggle(id: string) {
		checked = { ...checked, [id]: !checked[id] };
		if (mounted) {
			try {
				localStorage.setItem(STORAGE_KEY, JSON.stringify(checked));
			} catch {
				/* yoksay */
			}
		}
	}

	const done = $derived(TEST_SET.filter((e) => checked[e.id]).length);

	function fieldLabel(category: string): string {
		return t(`field.${category}`);
	}
</script>

<section class="panel card" aria-labelledby="testset-title">
	<div class="head">
		<div>
			<p class="kicker">{t('testSet.kicker')}</p>
			<h2 id="testset-title">{t('testSet.title')}</h2>
			<p class="small muted">{t('testSet.body')}</p>
		</div>
		<div class="progress" aria-live="polite">
			<span class="p-num">{done}<span class="p-total">/{TEST_SET.length}</span></span>
			<span class="p-label">{t('testSet.inspected')}</span>
			<div class="p-track" aria-hidden="true">
				<div class="p-fill" style:width="{(done / TEST_SET.length) * 100}%"></div>
			</div>
		</div>
	</div>

	<div class="table-wrap">
		<table>
			<thead>
				<tr>
					<th scope="col" class="check-col"><span class="sr-only">{t('testSet.inspected')}</span></th>
					<th scope="col">{t('testSet.colId')}</th>
					<th scope="col">{t('testSet.colArticle')}</th>
					<th scope="col">{t('testSet.colField')}</th>
					<th scope="col">{t('testSet.colFocus')}</th>
				</tr>
			</thead>
			<tbody>
				{#each TEST_SET as e (e.id)}
					<tr class:done={checked[e.id]}>
						<td class="check-col">
							<input
								type="checkbox"
								checked={!!checked[e.id]}
								onchange={() => toggle(e.id)}
								aria-label={t('testSet.markAria', { id: e.id })}
							/>
						</td>
						<td class="mono id">{e.id}</td>
						<td>
							<span class="title">{e.title}</span>
							<span class="meta">{e.authors} · {e.year} · {e.outlet}</span>
						</td>
						<td><span class="chip chip-neutral">{fieldLabel(e.category)}</span></td>
						<td class="focus">{e.focus}</td>
					</tr>
				{/each}
			</tbody>
		</table>
	</div>
	<p class="foot small muted">{t('testSet.foot')}</p>
</section>

<style>
	.panel {
		padding: 1.4rem 1.5rem;
	}
	.head {
		display: flex;
		justify-content: space-between;
		gap: 1.5rem;
		align-items: flex-start;
		margin-bottom: 1.1rem;
		flex-wrap: wrap;
	}
	.kicker {
		margin: 0 0 0.2rem;
		font-size: 0.7rem;
		font-weight: 700;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--accent-ink);
	}
	h2 {
		margin: 0 0 0.3rem;
	}
	.head p {
		margin: 0;
		max-width: 64ch;
	}
	.progress {
		min-width: 120px;
		text-align: right;
	}
	.p-num {
		font-family: var(--font-display);
		font-size: 1.8rem;
		line-height: 1;
	}
	.p-total {
		font-size: 1rem;
		color: var(--muted);
	}
	.p-label {
		display: block;
		font-size: 0.7rem;
		color: var(--muted);
		margin-bottom: 0.3rem;
	}
	.p-track {
		height: 5px;
		width: 120px;
		margin-left: auto;
		background: var(--paper-2);
		border-radius: 99px;
		overflow: hidden;
	}
	.p-fill {
		height: 100%;
		background: var(--accent);
		border-radius: inherit;
		transition: width 0.4s var(--ease);
	}
	.table-wrap {
		overflow-x: auto;
		border: 1px solid var(--line);
		border-radius: 11px;
	}
	table {
		width: 100%;
		border-collapse: collapse;
		font-size: 0.8rem;
	}
	th {
		text-align: left;
		font-size: 0.66rem;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: var(--muted);
		font-weight: 650;
		padding: 0.55rem 0.7rem;
		background: var(--paper);
		border-bottom: 1px solid var(--line);
		white-space: nowrap;
	}
	td {
		padding: 0.5rem 0.7rem;
		border-bottom: 1px solid var(--line);
		vertical-align: top;
	}
	tbody tr:last-child td {
		border-bottom: 0;
	}
	tr.done td {
		background: #f7fbf8;
	}
	tr.done .title {
		text-decoration: line-through;
		text-decoration-color: var(--line-strong);
		color: var(--muted);
	}
	.check-col {
		width: 36px;
		text-align: center;
	}
	.check-col input {
		accent-color: var(--accent);
		width: 15px;
		height: 15px;
	}
	.id {
		color: var(--muted);
		font-size: 0.72rem;
		white-space: nowrap;
	}
	.title {
		display: block;
		font-weight: 550;
		line-height: 1.35;
	}
	.meta {
		display: block;
		font-size: 0.7rem;
		color: var(--muted);
	}
	.focus {
		font-size: 0.76rem;
		color: var(--ink-2);
		max-width: 22ch;
	}
	.foot {
		margin: 0.7rem 0 0;
	}
</style>
